import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type { Role, User } from '@air/shared';

const VIEW_AS_KEY = 'air:viewAs';

function readStoredViewAs(): Role | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(VIEW_AS_KEY);
  if (v === 'employee' || v === 'it_reviewer' || v === 'legal_reviewer' || v === 'admin') return v;
  return null;
}

interface AuthState {
  user: User | null;
  status: 'loading' | 'anonymous' | 'authenticated';
  /** The real backend role for the signed-in user. Never derived. */
  realRole: Role | null;
  /**
   * The role the UI is rendering as. For admins this can be any role
   * (the role-switcher in the header). For everyone else this equals
   * realRole. Route guards and sidebar use this.
   */
  effectiveRole: Role | null;
  /** Admin-only. No-op for other roles. */
  setViewAs: (role: Role) => void;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [viewAs, setViewAsRaw] = useState<Role | null>(readStoredViewAs());

  async function refresh() {
    try {
      const me = await api.me();
      setUser(me);
      setStatus('authenticated');
      // If the stored viewAs is invalid for this user (non-admin), clear it.
      if (me.role !== 'admin' && viewAs !== null) {
        setViewAsRaw(null);
        window.localStorage.removeItem(VIEW_AS_KEY);
      }
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.error('auth.refresh failed', err);
      }
      setUser(null);
      setStatus('anonymous');
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setViewAs = useCallback(
    (role: Role) => {
      // Admin-only. No-op for everyone else (defense in depth — UI never offers the toggle either).
      if (user?.role !== 'admin') return;
      setViewAsRaw(role);
      window.localStorage.setItem(VIEW_AS_KEY, role);
    },
    [user?.role],
  );

  async function login(email: string, password: string): Promise<User> {
    const u = await api.login(email, password);
    setUser(u);
    setStatus('authenticated');
    // Clear any stale viewAs from a previous user.
    setViewAsRaw(null);
    if (typeof window !== 'undefined') window.localStorage.removeItem(VIEW_AS_KEY);
    return u;
  }

  async function logout(): Promise<void> {
    await api.logout();
    setUser(null);
    setStatus('anonymous');
    setViewAsRaw(null);
    if (typeof window !== 'undefined') window.localStorage.removeItem(VIEW_AS_KEY);
  }

  const realRole = user?.role ?? null;
  const effectiveRole: Role | null =
    user?.role === 'admin' && viewAs ? viewAs : realRole;

  return (
    <AuthCtx.Provider
      value={{ user, status, realRole, effectiveRole, setViewAs, login, logout, refresh }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
